import { useForm } from 'react-final-form';
import { useCallback, useMemo } from 'react';
import { BoxProps, useTheme } from '@mui/material';

import { useConfig } from '@occc/config';

import { TargetingAttributesFormValues } from '@occc-app/routes/Audience/types';
import { TargetingAttributesFormField } from '@occc-app/routes/Audience/constants';

import { FormFieldUIStatus, getFormFieldUIStatusColors, getFormFieldUIStatus } from './utils';
import { useSectionRefs } from './context/AudienceSectionRefsContext';
import { useExpandedSections } from './context/AudienceExpandedSectionsContext';
import { getOpenAndScrollToInvalidSection } from './context/utils';
import { getAudienceValidation } from './validation';

export interface ICollapsibleCardUIParams {
  iconColor: string;
  iconBackgroundColor: BoxProps['bgcolor'];
  formFieldUIStatus: FormFieldUIStatus;
}

export const useBuildCollapsibleCardParams = (): ((
  fields: TargetingAttributesFormField[],
) => ICollapsibleCardUIParams) => {
  const form = useForm<TargetingAttributesFormValues>();
  const theme = useTheme();

  return useCallback(
    (fields: TargetingAttributesFormField[]): ICollapsibleCardUIParams => {
      const formValues = form.getState().values;

      const formFieldUIStatuses: FormFieldUIStatus[] = fields.map((fieldName) => {
        const isArray = Array.isArray(formValues?.[fieldName]);

        return getFormFieldUIStatus({
          form,
          fieldName,
          isArray,
          formValues,
        });
      });

      const filledStatus = formFieldUIStatuses.find((value) => value === 'filled');
      const errorStatus = formFieldUIStatuses.find((value) => value === 'error');
      const formFieldUIStatus: FormFieldUIStatus = errorStatus || filledStatus || 'initial';

      const { iconColor, iconBackgroundColor } = getFormFieldUIStatusColors(
        theme,
        formFieldUIStatus,
      );
      return {
        iconColor,
        iconBackgroundColor,
        formFieldUIStatus,
      };
    },
    [form, theme],
  );
};

export const useOpenAndScrollToInvalidSection = () => {
  const sectionRefsContextValue = useSectionRefs();
  const expandedSectionsContextValue = useExpandedSections();

  const openAndScrollToInvalidSection = getOpenAndScrollToInvalidSection(
    sectionRefsContextValue,
    expandedSectionsContextValue,
  );

  return openAndScrollToInvalidSection;
};

export const useValidateAudience = () => {
  const { config } = useConfig();
  const areSubBrandsEnabled = !!config.subBrands?.enabled && !!config.subBrands?.list.length;

  return useMemo(() => getAudienceValidation(areSubBrandsEnabled), [areSubBrandsEnabled]);
};
